import waitForTimers from 'wait-for-timers';
import debugLog from './debug';
import { storageFactory } from './storage-factory';

export const safeLocalStorage = storageFactory(() => localStorage);

const log = (...args: any[]) => {
  debugLog({ logName: 'localstorage', logColor: 'Gray' }, ...args);
};

type JsonPrimitive = string | number | boolean | null;
interface JsonObject extends Record<string, JsonValue | undefined> {}
type JsonArray = JsonValue[];
type JsonValue = JsonPrimitive | JsonObject | JsonArray;
type PendingValue = JsonValue | (() => JsonValue) | typeof removeItemSymbol;

let pendingValues: Record<string, PendingValue> = {};
let ricId: number;
onTabHidden(saveLazyValuesNow);

// Symbol used to delete a key
export const removeItemSymbol = Symbol('delete');

export function getInitialValue(
  key: string,
  validate?: (arg: unknown) => boolean,
  defaultValue?: any
) {
  try {
    const stringValue = safeLocalStorage.getItem(`ap__${key}`) as string;
    // De-serialize from string
    const value = JSON.parse(stringValue);
    if (!validate || validate(value)) return value;
  } catch (err) {}

  return defaultValue;
}

export function getTypedInitialValue<T>(
  key: string,
  validate: (arg: unknown) => T,
  defaultValue: T
): T {
  let rawValue: string | null | undefined = undefined;
  let parsedValue: unknown;
  try {
    rawValue = safeLocalStorage.getItem(`ap__${key}`);
    if (rawValue) {
      // De-serialize from string
      parsedValue = JSON.parse(rawValue);
      return validate(parsedValue);
    }
  } catch (err) {
    // Avoid circular dependency by calling it on window
    window.reportSentryError(
      parsedValue === undefined
        ? new Error(`Failed to parse value for key "${key}"`, { cause: err })
        : new Error(`Validation error for key "${key}"`, { cause: err }),
      { key, rawValue, parsedValue }
    );
  }

  return defaultValue;
}

export function setLazyValue(key: string, value: PendingValue) {
  if (typeof window === 'undefined') return;

  pendingValues[key] = value;

  // Clear any pending save
  cancelIdleCallback(ricId);

  // Queue new save
  ricId = requestIdleCallback(saveLazyValuesNow);
}

function saveLazyValuesNow() {
  Object.entries(pendingValues).forEach(([key, value]) => {
    // Allow value to be a function (to delay expensive execution)
    if (typeof value === 'function') value = value();
    // Delete if a delete symbol is passed
    if (value === removeItemSymbol) {
      safeLocalStorage.removeItem(`ap__${key}`);
      return;
    }
    // Serialize to string
    const serializedValue = JSON.stringify(value);
    // Store safely
    safeLocalStorage.setItem(`ap__${key}`, serializedValue);
  });

  pendingValues = {};
}

let wasHiddenRecently = false;
export function wasRecentlyHidden() {
  return wasHiddenRecently;
}

export function onTabHidden(callback: () => void) {
  if (typeof window === 'undefined') return;

  // Save everything if user changes tabs
  addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      wasHiddenRecently = true;
      setTimeout(() => (wasHiddenRecently = false), 1000);
      callback();
    }
  });

  // In Safari, we also need
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  if (isSafari) {
    addEventListener('beforeunload', () => {
      wasHiddenRecently = true;
      callback();
    });
  }
}

// TEMP - Cleanup lscache-* LocalStorage values (released April 22, only iOS alpha is in people's hands)
if (typeof window !== 'undefined') {
  waitForTimers([1000 * 10, 'ric'], () => {
    const keys: string[] = [];
    for (let i = 0; i < safeLocalStorage.length; i++) {
      const key = safeLocalStorage.key(i)!;
      if (key.startsWith('lscache-')) keys.push(key);
    }
    if (keys.length) {
      waitForTimers(['ric'], () => {
        log('Cleaning up lscache- keys', keys);
        keys.forEach((key) => safeLocalStorage.removeItem(key));
      });
    }
  });
}
